<template>
  <div class="popup_overlay px-4">
    <div class="custom_dialog rounded-xl" style="width: 600px; max-height: 92vh;">
      <!-- Header -->
      <div class="bg-primary flex p-3 text-center justify-center sticky top-0 z-20">
        <div class="font-medium text-white heading-3">Add Address</div>
      </div>

      <div class="overflow-auto p-4" style="max-height: calc(92vh - 120px);">
        <!-- Search address -->
        <div class="relative mb-3">
          <p class="label_css">Search address</p>
          <input
            type="text"
            v-model="location"
            placeholder="Search address"
            class="outline-none border border-gray2 text-text1 rounded cust_text_field w-full pl-3 p-2.5 focus:border-primary focus:ring-1 focus:ring-primary"
            style="height: 50px;"
          />
          <div
            v-if="isShowSelection && searchResults.length"
            class="card p-2 absolute z-30 bg-white w-full shadow-lg"
            style="top: 100%;"
            v-click-outside="closeList"
          >
            <div
              v-for="result in searchResults"
              :key="result.place_id"
              @click="saveLocation(result)"
              class="flex hover:bg-gray-100 cursor-pointer py-2"
            >
              <div class="items-center grid w-14 mr-2">
                <div class="text-center">
                  <i class="fas fa-map-marker-alt text-primary pr-3 pl-2"></i>
                </div>
                <div class="heading-7 text-center text-gray3">
                  {{ getDistance(parseInt(result.distance_meters)) }}
                </div>
              </div>
              <div>
                <div class="cursor-pointer text-text1 font-semibold">
                  {{ result.structured_formatting.main_text }}
                </div>
                <div class="cursor-pointer text-text2">
                  {{ result.structured_formatting.secondary_text }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Map -->
        <div class="mb-2">
          <gmap-map
            v-if="addressObj.isShow"
            :center="addressObj.center"
            :zoom="16"
            @click="mark"
            style="width:100%; height: 350px;"
          >
            <gmap-marker :position="addressObj.center"></gmap-marker>
          </gmap-map>
          <div v-if="addressObj.isShow" class="pt-1">
            <p class="text-primary italic flex items-center heading-7">
              Click on the map to select or adjust the address location
            </p>
          </div>
        </div>

        <!-- Full Address Display -->
        <div v-if="addressObj.fullAddress" class="mb-4">
          <p class="text-text1 heading-4 font-medium" style="word-wrap: break-word;">
            {{ addressObj.fullAddress }}
          </p>
        </div>

        <!-- Manual Address Fields -->
        <div v-if="addressObj.isShow">
          <div class="flex gap-3 mb-3">
            <div class="flex-1">
              <p class="label_css">House/Office number</p>
              <input
                type="text"
                v-model="houseNumber"
                placeholder="House/Office number"
                class="outline-none border border-gray2 text-text1 rounded cust_text_field w-full pl-3 p-2.5 focus:border-primary focus:ring-1 focus:ring-primary"
                style="height: 50px;"
              />
            </div>
            <div class="flex-1">
              <p class="label_css">Floor number</p>
              <input
                type="text"
                v-model="floorNumber"
                placeholder="Floor number"
                class="outline-none border border-gray2 text-text1 rounded cust_text_field w-full pl-3 p-2.5 focus:border-primary focus:ring-1 focus:ring-primary"
                style="height: 50px;"
              />
            </div>
          </div>
          <div class="mb-2">
            <p class="label_css">Apartment/Building name</p>
            <input
              type="text"
              v-model="buildingName"
              placeholder="Apartment/Building name"
              class="outline-none border border-gray2 text-text1 rounded cust_text_field w-full pl-3 p-2.5 focus:border-primary focus:ring-1 focus:ring-primary"
              style="height: 50px;"
            />
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="sticky bottom-0 bg-white px-4 py-3 flex items-center justify-end border-t">
        <div class="flex gap-2">
          <Button :btnSize="'medium'" :textColor="'text1'" :btnColor="'gray1'" :btnText="'Discard'" @buttonAction="$emit('close')"/>
          <Button :btnSize="'medium'" :textColor="'white'" :btnColor="'primary'" :btnText="'Save'" @buttonAction="saveAddress"/>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* global google */
import Button from '@/View/components/globalButton.vue'
import store from '@/store'
import Vue from 'vue'
import * as VueGoogleMaps from 'vue2-google-maps'

Vue.use(VueGoogleMaps, {
  load: {
    key: store.getters.googleMapApiKey,
    libraries: 'places,drawing',
    language: 'en'
  }
})

export default {
  components: { Button },
  props: ['prefillData'],
  data: () => ({
    location: '',
    searchResults: [],
    isShowSelection: false,
    autocompleteService: null,
    placesService: null,
    biasCircle: null,
    addressObj: {
      isShow: false,
      center: null,
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      zip: '',
      country: '',
      lat: null,
      lng: null,
      fullAddress: '',
    },
    houseNumber: '',
    floorNumber: '',
    buildingName: '',
    debounceTimer: null,
    isSelectingPlace: false,
  }),

  watch: {
    location(newVal) {
      if (this.isSelectingPlace) return
      if (!newVal) {
        this.searchResults = []
        this.isShowSelection = false
        return
      }
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => this.performAutocomplete(newVal), 300)
    }
  },

  mounted() {
    this.$gmapApiPromiseLazy().then(() => {
      this.autocompleteService = new google.maps.places.AutocompleteService()
      this.placesService = new google.maps.places.PlacesService(
        new google.maps.Map(document.createElement('div'))
      )
      this.setBiasLocation()
    })

    if (this.prefillData) {
      this.location = this.prefillData.fullAddress || ''
      this.addressObj = {
        isShow: !!(this.prefillData.lat && this.prefillData.lng),
        center: this.prefillData.lat ? { lat: this.prefillData.lat, lng: this.prefillData.lng } : null,
        addressLine1: this.prefillData.addressLine1 || '',
        addressLine2: this.prefillData.addressLine2 || '',
        city: this.prefillData.city || '',
        state: this.prefillData.state || '',
        zip: this.prefillData.zip || '',
        country: this.prefillData.country || '',
        lat: this.prefillData.lat || null,
        lng: this.prefillData.lng || null,
        fullAddress: this.prefillData.fullAddress || '',
      }
    }
  },

  methods: {
    setBiasLocation() {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          pos => this.createBiasCircle(pos.coords.latitude, pos.coords.longitude),
          () => this.fallbackOrgLocation()
        )
      } else {
        this.fallbackOrgLocation()
      }
    },

    fallbackOrgLocation() {
      const org = JSON.parse(localStorage.getItem('orgInfo'))
      if (org?.latitude && org?.longitude) {
        this.createBiasCircle(org.latitude, org.longitude)
      }
    },

    createBiasCircle(lat, lng) {
      this.biasCircle = new google.maps.Circle({
        center: new google.maps.LatLng(lat, lng),
        radius: 50000
      })
    },

    performAutocomplete(input) {
      if (!this.autocompleteService || !input.trim()) return
      const request = {
        input,
        fields: ['place_id', 'structured_formatting'],
        ...(this.biasCircle && { locationBias: this.biasCircle })
      }
      this.autocompleteService.getPlacePredictions(request, async (predictions, status) => {
        if (status !== google.maps.places.PlacesServiceStatus.OK || !predictions) {
          this.searchResults = []
          this.isShowSelection = false
          return
        }
        const userLocation = this.biasCircle ? this.biasCircle.getCenter().toJSON() : null
        this.searchResults = await Promise.all(
          predictions.map(prediction =>
            new Promise(resolve => {
              this.placesService.getDetails(
                { placeId: prediction.place_id, fields: ['geometry'] },
                (details, detailStatus) => {
                  let distance = null
                  if (detailStatus === google.maps.places.PlacesServiceStatus.OK && details.geometry && userLocation) {
                    distance = this.computeDistance(userLocation, {
                      lat: details.geometry.location.lat(),
                      lng: details.geometry.location.lng()
                    })
                  }
                  resolve({ ...prediction, distance_meters: distance })
                }
              )
            })
          )
        )
        this.isShowSelection = true
      })
    },

    computeDistance(a, b) {
      const R = 6371e3
      const toRad = x => (x * Math.PI) / 180
      const dLat = toRad(b.lat - a.lat)
      const dLon = toRad(b.lng - a.lng)
      const x =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2)
      return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x))
    },

    async saveLocation(prediction) {
      this.isSelectingPlace = true
      this.isShowSelection = false
      this.searchResults = []
      try {
        const place = await this.getPlaceDetails(prediction.place_id)
        this.setPlace(place)
      } catch (e) {
        console.error('Place details error:', e)
      }
      setTimeout(() => { this.isSelectingPlace = false }, 100)
    },

    getPlaceDetails(placeId) {
      return new Promise((resolve, reject) => {
        this.placesService.getDetails({ placeId }, (result, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK) {
            resolve(result)
          } else {
            reject(status)
          }
        })
      })
    },

    setPlace(place) {
      this.addressObj.center = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng()
      }
      this.addressObj.isShow = true
      this.addressObj.lat = place.geometry.location.lat()
      this.addressObj.lng = place.geometry.location.lng()
      this.addressObj.addressLine2 = ''
      this.addressObj.city = ''
      this.addressObj.state = ''
      this.addressObj.zip = ''
      this.addressObj.country = ''

      const sublocalityParts = []
      for (const comp of place.address_components) {
        const type = comp.types[0]
        if (['sublocality_level_2', 'sublocality_level_1', 'sublocality'].includes(type)) {
          sublocalityParts.push(comp.long_name)
        }
        if (type === 'locality') this.addressObj.city = comp.short_name
        if (type === 'administrative_area_level_1') this.addressObj.state = comp.short_name
        if (type === 'administrative_area_level_2') this.addressObj.addressLine2 = comp.long_name
        if (type === 'postal_code') this.addressObj.zip = comp.long_name
        if (type === 'country') this.addressObj.country = comp.short_name
      }

      this.addressObj.addressLine1 = sublocalityParts.join(' ').trim() || place.name || ''
      this.addressObj.fullAddress = place.formatted_address
      this.isSelectingPlace = true
      this.location = this.addressObj.fullAddress
      this.$nextTick(() => { this.isSelectingPlace = false })
    },

    mark(event) {
      const latlng = { lat: event.latLng.lat(), lng: event.latLng.lng() }
      const geocoder = new google.maps.Geocoder()
      geocoder.geocode({ location: latlng }, (results, status) => {
        if (status === 'OK' && results[1]) {
          this.addressObj.fullAddress = results[1].formatted_address
          this.location = results[1].formatted_address
        }
        this.addressObj.center = latlng
        this.addressObj.lat = latlng.lat
        this.addressObj.lng = latlng.lng
      })
    },

    getDistance(distanceMeter) {
      if (isNaN(distanceMeter)) return '--'
      const isUS = navigator.languages?.[0]?.startsWith('en-US') || false
      const miles = distanceMeter * 0.00062137
      const km = distanceMeter / 1000
      if (isUS) return miles >= 1000 ? '999+ mi' : `${miles.toFixed(1)} mi`
      return km >= 1000 ? '999+ km' : `${km.toFixed(1)} km`
    },

    closeList() {
      this.isShowSelection = false
    },

    saveAddress() {
      const addressLine1Parts = [this.houseNumber, this.buildingName, this.addressObj.addressLine1]
        .filter(Boolean).join(', ')
      this.$emit('addressConfirmed', {
        addressLine1: addressLine1Parts || this.addressObj.addressLine1,
        addressLine2: this.addressObj.addressLine2,
        city: this.addressObj.city,
        state: this.addressObj.state,
        zip: this.addressObj.zip,
        country: this.addressObj.country,
        lat: this.addressObj.lat,
        lng: this.addressObj.lng,
        fullAddress: this.addressObj.fullAddress,
        houseNumber: this.houseNumber,
        floorNumber: this.floorNumber,
        buildingName: this.buildingName,
      })
    }
  }
}
</script>
